import React from 'react';
import { MarkdownRenderer } from "../MarkdownRenderer";
import { DataTable } from "../../ui/datatable/datatable";
import { ColumnDef } from "@tanstack/react-table";
import { DefaultToolCallRenderer } from '../ToolRenderers';
import { Badge } from "../../ui/badge";
import { FileIcon, FolderIcon, GitBranch, GithubIcon, Search } from 'lucide-react';

// Message interface for the SearchCodeToolRenderer
interface Message {
    role: 'user' | 'assistant' | 'system' | 'tool';
    content: string;
    tool_name?: string;
    tool_call?: boolean;
    tool_call_id?: string;
    tool_call_function_name?: string;
    tool_call_function_args?: string;
    tool_call_response_id?: string;
    tool_call_response_content?: string;
    screenshot?: string;
}

// ToolRendererProps interface
export interface ToolRendererProps {
    message: Message;
    isToolCall: boolean;
}

// Repository owner structure
interface RepositoryOwner {
    login: string;
    id: number;
    avatar_url: string;
    html_url: string;
    type: string;
}

// Repository structure
interface Repository {
    id: number;
    name: string;
    full_name: string;
    private: boolean;
    owner: RepositoryOwner;
    html_url: string;
    description: string;
    fork: boolean;
}

// Search result item structure
interface SearchResultItem {
    name: string;
    path: string;
    sha: string;
    html_url: string;
    repository: Repository;
}

// Search code response structure
interface SearchCodeResponse {
    total_count: number;
    incomplete_results: boolean;
    items: SearchResultItem[];
}

// Search code parameters structure
interface SearchCodeParams {
    query: string;
    repository?: string;
    language?: string;
    path?: string;
    per_page?: number;
}

// Function to extract file extension
const getFileExtension = (filename: string): string => {
    const parts = filename.split('.');
    return parts.length > 1 ? parts[parts.length - 1].toLowerCase() : '';
};

// Function to get icon based on file extension
const getFileIcon = (filename: string): JSX.Element => {
    const ext = getFileExtension(filename);
    return <FileIcon className="h-4 w-4" />;
};

// Tool renderer for code search results
export function SearchCodeToolRenderer({ message, isToolCall }: ToolRendererProps) {
    // Parse the search parameters
    let searchParams: SearchCodeParams | null = null;
    try {
        if (message.tool_call_function_args) {
            searchParams = JSON.parse(message.tool_call_function_args);
        }
    } catch (error) {
        console.error('Error parsing search parameters:', error);
    }

    // If this is just the tool call and not the response, render the default tool call renderer
    if (!message.tool_call_response_content) {
        return <DefaultToolCallRenderer message={message} />;
    }

    let response: SearchCodeResponse | null = null;
    try {
        response = JSON.parse(message.tool_call_response_content);
    } catch (error) {
        console.error('Error parsing search code response data:', error);
        return null;
    }

    // Validate response
    if (!response || !response.items || !Array.isArray(response.items)) {
        return null;
    }

    // Define columns for the DataTable
    const columns: ColumnDef<SearchResultItem>[] = [
        {
            id: "file",
            header: "File",
            cell: ({ row }) => {
                const item = row.original;
                return (
                    <div className="flex items-center gap-2">
                        {getFileIcon(item.name)}
                        <a 
                            href={item.html_url} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-primary hover:underline"
                        >
                            {item.name}
                        </a>
                    </div>
                );
            },
        },
        {
            id: "path",
            accessorKey: "path",
            header: "Path",
            cell: ({ row }) => {
                const path = row.original.path;
                const displayPath = path.length > 30 ? truncateMiddle(path, 30) : path;
                return (
                    <div className="flex items-center gap-1">
                        <FolderIcon className="h-4 w-4 text-textmedium" />
                        <span title={path}>{displayPath}</span>
                    </div>
                );
            },
        },
        {
            id: "repository",
            header: "Repository",
            cell: ({ row }) => {
                const repo = row.original.repository;
                return (
                    <div className="flex items-center gap-2">
                        <GithubIcon className="h-4 w-4" />
                        <a 
                            href={repo.html_url} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-primary hover:underline"
                        >
                            {repo.full_name}
                        </a>
                    </div>
                );
            },
        },
    ];

    // No filters for this table
    const filters: { filteringColumn: string; title: string; }[] = [];

    // Create a title that includes the result count
    const tableTitle = `Code Search Results (${response.total_count})`;

    // Build search parameter badges
    const searchParamBadges = [];
    if (searchParams) {
        if (searchParams.query) {
            searchParamBadges.push(
                <Badge key="query" className="bg-primary/20 text-primary hover:bg-primary/20 flex items-center gap-1">
                    <Search className="h-3 w-3" />
                    {searchParams.query}
                </Badge>
            );
        }
        if (searchParams.repository) {
            searchParamBadges.push(
                <Badge key="repo" className="bg-blue-500/20 text-blue-500 hover:bg-blue-500/20 flex items-center gap-1">
                    <GithubIcon className="h-3 w-3" />
                    {searchParams.repository}
                </Badge>
            );
        }
        if (searchParams.language) {
            searchParamBadges.push(
                <Badge key="lang" className="bg-purple-500/20 text-purple-500 hover:bg-purple-500/20">
                    {searchParams.language}
                </Badge>
            );
        }
        if (searchParams.path) {
            searchParamBadges.push(
                <Badge key="path" className="bg-green-500/20 text-green-500 hover:bg-green-500/20 flex items-center gap-1">
                    <FolderIcon className="h-3 w-3" />
                    {searchParams.path}
                </Badge>
            );
        }
    }

    return (
        <div className="w-full">
            <MarkdownRenderer content={message.content} />
            
            {searchParamBadges.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-2">
                    <span className="text-sm font-medium text-textmedium">Search parameters:</span>
                    {searchParamBadges}
                </div>
            )}
            
            <div className="w-full border rounded">
                <DataTable
                    columns={columns}
                    data={response.items}
                    tableTitle={tableTitle}
                    filters={filters}
                    showPagination={true}
                    removeBorder={true}
                    textFilteringColumnName="path"
                    textFilteringPlaceholder="Filter by path..."
                    columnConfigs={[
                        { id: "path", isFilterable: true }
                    ]}
                />
            </div>
        </div>
    );
}

// Add truncateMiddle function if not already defined in your codebase
const truncateMiddle = (str: string, maxLength: number): string => {
    if (str.length <= maxLength) return str;
    
    const halfLength = Math.floor((maxLength - 3) / 2);
    const start = str.slice(0, halfLength);
    const end = str.slice(str.length - halfLength);
    
    return `${start}...${end}`;
};

// Static method to determine if this renderer should render a message
SearchCodeToolRenderer.shouldRender = (message: Message): boolean => {
    return true;
};